import { reportError } from '../core/telemetry/report';
import React from 'react';

/**
 * A driver in a hospital basement must NEVER get a blank screen. React 18
 * unmounts the whole tree on an uncaught render error, which renders as a
 * white page with no text, no button and nothing to report to dispatch --
 * indistinguishable from a dead phone.
 *
 * This catches that and shows something actionable, including the error text
 * so a driver can read it down the phone to dispatch.
 */
interface State { error: Error | null }

export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State { return { error }; }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    /*
     * Report it. A React error boundary swallows the error before it reaches
     * window.onerror, so the crash reporter listening there never saw a single
     * render crash — including the one that stopped a driver dead on the name
     * screen. Without this the telemetry is blind to the most common failure
     * there is.
     */
    reportError('crash', location.pathname.split('/').filter(Boolean)[0] ?? 'home',
                `${error.message} | ${info.componentStack?.slice(0, 300) ?? ''}`);
    // Kept in the console so a plugged-in phone can be inspected.
    console.error('render_error', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <main className="screen">
        <h1>Something went wrong</h1>
        <p className="hint">
          The app hit an error and could not show this screen. Your queued
          deliveries are safe and will still sync.
        </p>
        <div className="banner error-banner">{this.state.error.message}</div>
        <div className="col actions">
          <button className="btn primary big" onClick={() => window.location.reload()}>
            Reload the app
          </button>
          <button className="btn secondary" onClick={() => {
            window.location.assign('/route');
          }}>
            Back to my route
          </button>
        </div>
        <p className="hint">If this keeps happening, call dispatch and read them the message above.</p>
      </main>
    );
  }
}
